import { useRef } from 'preact/hooks'
import classNames from 'classnames'

import CTA from '@commons/cta/CTA.tsx'
import Link from '@commons/link/Link.tsx'

import Video from '@commons/video/Video'
import { getPBProps } from '@content/helpers/src/utils'
import { BannerPBProps } from './banner.types'
import BannerEventInfo from './subcomponents/BannerEventInfo/BannerEventInfo'
import BannerOverlay from './subcomponents/BannerOverlay/BannerOverlay'
import useImpressionTracker from '@content/helpers/src/hooks/useImpressionTracker'
import './Banner.scss'

type BannerProps = {
    readonly data: string
}

const Banner = ({ data }: BannerProps) => {
    const pbProps = getPBProps<BannerPBProps>(data)
    const {
        layout,
        config: {
            bannerType = 'event',
            eventAlignment = '',
            masterclassAlignment = '',
            prefersContrast = ''
        } = {},
        copywriting: {
            heading = '',
            label = '',
            eventInfo,
            image,
            video,
            a11y_video_description,
            cta,
            link
        },
        campaign
    } = pbProps
    const containerRef = useRef(null)

    useImpressionTracker(containerRef, campaign)

    return (
        <div
            ref={containerRef}
            className="fs-banner"
            style={
                !video?.url
                    ? {
                          backgroundImage: `url(${image?.desktop?.src})`
                      }
                    : {}
            }>
            {video?.url && (
                <Video
                    url={video.url}
                    auto_play={video?.config?.auto_play}
                    a11y_video_description={a11y_video_description}
                />
            )}

            <BannerOverlay
                backgroundColor={prefersContrast ? 'rgba(0, 0, 0, 0.7)' : null}
            />
            <div
                className={classNames(
                    'fs-banner__inner',
                    layout.padding_bottom,
                    layout.padding_top,
                    {
                        'fs-banner__inner--center':
                            bannerType === 'event' &&
                            eventAlignment === 'center',
                        'fs-banner__inner--right':
                            bannerType === 'masterclass' &&
                            masterclassAlignment === 'right'
                    }
                )}>
                <div
                    className={classNames('fs-banner__content', {
                        'fs-banner__content--masterclass':
                            bannerType === 'masterclass'
                    })}>
                    {bannerType === 'event' ? (
                        <>
                            <span className="fs-banner__label">{label}</span>
                            <h2>{heading}</h2>
                            <BannerEventInfo {...eventInfo} />

                            <CTA
                                {...cta}
                                defaultVariant="secondary"
                                campaign={campaign}
                                tracking_select_promotion={`LINK - 1 - ${cta?.label}`}
                            />
                            {link?.link && (
                                <Link
                                    {...link}
                                    campaign={campaign}
                                    tracking_select_promotion={`LINK - 2 - ${link.label}`}
                                    className="fs-banner__link"
                                />
                            )}
                        </>
                    ) : (
                        <>
                            <div className="fs-banner__details">
                                <span className="fs-banner__label">
                                    {label}
                                </span>
                                <h1>{heading}</h1>
                                <BannerEventInfo {...eventInfo} />

                                <CTA
                                    {...cta}
                                    campaign={campaign}
                                    tracking_select_promotion={`LINK - 1 - ${cta?.label}`}
                                />

                                {link?.link && (
                                    <Link
                                        {...link}
                                        campaign={campaign}
                                        tracking_select_promotion={`LINK - 2 - ${link.label}`}
                                        className="fs-banner__link"
                                    />
                                )}
                            </div>
                            <BannerEventInfo {...eventInfo} desktop />
                        </>
                    )}
                </div>
            </div>
        </div>
    )
}

export default Banner
